import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Filter, ClipboardList, XCircle } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { PaginationBar, EmptyState, LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 20;
const STATUSES = ["assigned", "not_started", "in_progress", "completed", "overdue", "cancelled"];

export default function OrgAssignmentsPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [courseId, setCourseId] = useState("all");
  const [status, setStatus] = useState("all");
  const [mand, setMand] = useState("all");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (courseId !== "all") params.course_id = courseId;
      if (status !== "all") params.status = status;
      if (mand !== "all") params.mandatory_or_optional = mand;
      const { data } = await api.get("/org/course-assignments", { params });
      setRows(data.data.assignments); setTotal(data.data.total);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [page, search, courseId, status, mand]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/org/course-mappings");
        setCourses(data.data.mappings.map((m) => m.course));
      } catch { /* silent */ }
    })();
  }, []);

  const cancel = async (id) => {
    try {
      await api.patch(`/org/course-assignments/${id}/status`, { status: "cancelled" });
      toast.success("Assignment cancelled.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardLayout title="Course assignments" breadcrumbs={["My Institute", "Assignments"]}>
      <div className="space-y-4" data-testid="org-assignments">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search staff by name / email / emp ID" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="assign-search" />
          </div>
          <Select value={courseId} onValueChange={(v) => { setCourseId(v); setPage(1); }}>
            <SelectTrigger className="w-64"><SelectValue placeholder="Course" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All courses</SelectItem>
              {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.course_title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={mand} onValueChange={(v) => { setMand(v); setPage(1); }}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="mandatory">Mandatory</SelectItem>
              <SelectItem value="optional">Optional</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white">
          {loading ? <LoadingBlock label="Loading assignments…" /> : rows.length === 0 ? (
            <EmptyState title="No assignments" description="Assign a course to your staff to see it here." icon={ClipboardList} />
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Staff</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Course</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Type</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Priority</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Due</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>
                      <div className="text-sm font-semibold text-slate-900">{a.user?.full_name || "—"}</div>
                      <div className="text-xs text-slate-500">{a.user?.email}</div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{a.course?.course_title || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-600 capitalize">{a.assignment_type?.replace(/_/g, " ")}</TableCell>
                    <TableCell>
                      {a.mandatory_or_optional === "mandatory" ? (
                        <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700">Mandatory</span>
                      ) : (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600">Optional</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{a.due_date || "—"}</TableCell>
                    <TableCell><StatusBadge status={a.status} /></TableCell>
                    <TableCell className="text-right">
                      {a.status !== "cancelled" && a.status !== "completed" && (
                        <button onClick={() => cancel(a.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:text-rose-800">
                          <XCircle className="h-3.5 w-3.5" /> Cancel
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <PaginationBar page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testKey="assign-pagination" />
        </div>
      </div>
    </DashboardLayout>
  );
}
